<script lang="ts">
import { isPreviewing } from '@builder.io/sdk-svelte';
import { building } from '$app/environment';
import { replaceState } from '$app/navigation';
import { page } from '$app/state';
import type { LinkProps } from '$lib/types';
import { formatBlogDate } from '$lib/utils/dates.js';
import {
    newsCategoryMatches,
    normalizeNewsCategoryFilter,
    toNewsCategoryOptions,
} from '$lib/utils/newsCategoryFilters.js';
import { BLOG_NAME } from '../../config/constants.js';
import { ERoutes } from '../../config/enums.js';
import CardGridItem from '../../stories/atoms/CardGridItem/CardGridItem.svelte';
import BlogCategoryNav from '../../stories/molecules/BlogCategoryNav/BlogCategoryNav.svelte';
import CardGrid from '../../stories/molecules/CardGrid/CardGrid.svelte';
import Hero from '../../stories/organisms/Hero/Hero.svelte';

interface CategoryEntity {
    id?: string;
    name?: string;
}

interface Article {
    id?: string;
    name?: string;
    data: {
        slug?: string;
        date: string;
        thumbnail?: string;
        category?: {
            id?: string;
            value?: {
                id?: string;
                name?: string;
            };
        };
    };
}

// biome-ignore lint/suspicious/noExplicitAny: Data type from +page.server.js
let { data }: { data: any } = $props();

let currentCategory = $state('');

// shortcut to the actual content
let pageContent = $derived(data.builderProps.content?.data || {});

// we want to show unpublished content when in preview mode.
let canShowContent = $derived(pageContent || isPreviewing());

let categories: LinkProps[] = $state([]);
// During build/prerendering, searchParams throws an error - use empty URLSearchParams
let currentSearchParams: URLSearchParams;
try {
    currentSearchParams = building ? new URLSearchParams() : page.url.searchParams;
} catch {
    currentSearchParams = new URLSearchParams();
}

const onClick = (e: MouseEvent) => {
    e.preventDefault();

    const url = new URLSearchParams(currentSearchParams);
    const destination = (e.currentTarget as HTMLAnchorElement).getAttribute('href') ?? '';
    const category = new URL(destination, window.location.origin).searchParams.get('category');

    if (!category) {
        url.delete('category');
    } else {
        url.set('category', normalizeNewsCategoryFilter(category));
    }
    currentSearchParams = url;
    replaceState(`?${url}`, history.state);
    updateLinks(url.toString());
};

const updateLinks = (currentUrl: string) => {
    categories = toNewsCategoryOptions(
        (data.allCategories as CategoryEntity[] | undefined) ?? [],
    ).map((category) => ({
        text: category.name,
        to: `${ERoutes.TheWire}?category=${category.id}`,
        active: false, // Will be set correctly below
        onClick,
    }));

    const params = new URLSearchParams(currentUrl);
    const categoryParam = params.get('category');
    currentCategory = normalizeNewsCategoryFilter(categoryParam);

    // Set active states based on current category
    if (categories) {
        categories.forEach((cat) => {
            const categoryQuery = new URLSearchParams(cat.to?.split('?')[1] ?? '').get('category');
            cat.active = categoryQuery === currentCategory;
        });
    }

    // add landing link as first element
    categories.splice(0, 0, {
        text: 'All',
        to: ERoutes.TheWire,
        active: !currentCategory || currentCategory === '',
        onClick,
    });
};

// During build/prerendering, use empty string to avoid accessing page.url
let initialSearch = '';
try {
    initialSearch = building ? '' : (page?.url?.search ?? '');
} catch {
    // Silently fail during prerendering
}
updateLinks(initialSearch);

let listItems = $derived(
    (data.allPosts as Article[] | undefined)?.filter((item) => {
        if (currentCategory !== '') {
            const itemCategoryId = item.data?.category?.id ?? item.data?.category?.value?.id;
            if (!newsCategoryMatches(itemCategoryId, currentCategory)) {
                return false;
            }
        }
        return true;
    }),
);
</script>

{#if canShowContent}
    <!-- force re-rendering of `Content` on client-side navigation -->
    {#key data.builderProps}
        <Hero
            image={{
                src: pageContent?.headerImage,
                altText: pageContent?.metaTitle,
            }}
            mobileImage={{
                src: pageContent?.headerImageMobile,
                alt: pageContent?.metaTitle,
            }}
            content={BLOG_NAME}
            mastheadTextColour={'#fff'}
        />

        <div class="scroll-content-over-header">
            <BlogCategoryNav links={categories} />

            <CardGrid hasBorder={false} hasBottomMargin={false}>
                {#snippet children()}
                    {#each listItems ?? [] as post (post.id)}
                        <CardGridItem
                            data-aos="fade-up"
                            class="builder-block"
                            to={`${ERoutes.TheWire}${post.data?.slug ?? ''}`}
                            title={post.name}
                            description={formatBlogDate(post.data?.date)}
                            image={{
                                src: post.data?.thumbnail,
                                altText: post.name,
                            }}
                        />
                    {/each}
                {/snippet}
            </CardGrid>
        </div>
    {/key}
{:else}
    Content Not Found
{/if}

<style lang="scss">
    @use '/src/scss/global-constants/mq-config';
    @use 'sass-mq/mq' as *;
    .scroll-content-over-header {
        background: var(--colour-white);
        position: relative;
        z-index: 1;
    }

</style>
